import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Pencil, Plus, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { timerAPI, timeTrackingAPI, tasksAPI } from '../lib/api';
import { useTimerStore, fmtMinutes } from '../lib/timerStore';
import { useAuthStore } from '../store';

const hm = (iso) => (iso ? new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '');

const LogRow = ({ l, onSaved }) => {
  const [edit, setEdit] = useState(false);
  const [min, setMin] = useState(String(l.duration_minutes));
  const save = async () => {
    try { await timeTrackingAPI.updateLog(l.id, Number(min)); toast.success('Registro corregido'); setEdit(false); onSaved(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 py-2 border-b last:border-0 text-sm" data-testid={`my-log-${l.id}`}>
      <div className="flex-1 min-w-[180px]">
        <p className="font-medium truncate">{l.task_title || 'Tarea'}</p>
        <p className="text-xs text-muted-foreground">{l.client_name}{l.started_at ? ` · ${hm(l.started_at)}–${hm(l.ended_at)}` : ''}</p>
      </div>
      {l.needs_review && <Badge className="bg-amber-100 text-amber-800 border-amber-300 gap-1" data-testid={`my-log-review-${l.id}`}><AlertTriangle className="w-3 h-3" />Para revisar</Badge>}
      {l.edited_manually && <Badge variant="outline" data-testid={`my-log-edited-${l.id}`}>Editado a mano</Badge>}
      {edit ? (
        <span className="flex items-center gap-1">
          <Input type="number" min="1" value={min} onChange={(e) => setMin(e.target.value)} className="h-8 w-20" data-testid={`my-log-input-${l.id}`} />min
          <Button size="sm" variant="ghost" onClick={save} data-testid={`my-log-save-${l.id}`}>Guardar</Button>
        </span>
      ) : (
        <span className="flex items-center gap-1 font-semibold">{fmtMinutes(l.duration_minutes)}
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEdit(true)} data-testid={`my-log-edit-${l.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
        </span>
      )}
    </div>
  );
};

const AddManual = ({ onSaved }) => {
  const [tasks, setTasks] = useState([]);
  const [taskId, setTaskId] = useState('');
  const [min, setMin] = useState('');
  const me = useAuthStore((s) => s.user?.id);
  useEffect(() => {
    tasksAPI.getAll().then((t) => setTasks((t || []).filter((x) => x.assigned_to === me && x.status !== 'completed' && x.project_id && x.project_id !== 'none'))).catch(() => {});
  }, [me]);
  const add = async () => {
    try { await timerAPI.manualLog({ task_id: taskId, duration_minutes: Number(min) }); toast.success('Tiempo añadido'); setMin(''); setTaskId(''); onSaved(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid="my-time-add">
      <p className="text-sm font-medium">¿Se te olvidó arrancarlo? Añade el tiempo</p>
      <div className="flex gap-2">
        <select value={taskId} onChange={(e) => setTaskId(e.target.value)} className="flex-1 border rounded-md px-2 h-9 text-sm bg-background" data-testid="my-time-add-task">
          <option value="">Elige la tarea…</option>
          {tasks.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
        </select>
        <Input type="number" min="1" placeholder="min" value={min} onChange={(e) => setMin(e.target.value)} className="w-20 h-9" data-testid="my-time-add-min" />
        <Button size="sm" className="h-9" disabled={!taskId || !(Number(min) > 0)} onClick={add} data-testid="my-time-add-btn"><Plus className="w-4 h-4" /></Button>
      </div>
      <p className="text-[11px] text-muted-foreground">Quedará marcado como "Editado a mano".</p>
    </div>
  );
};

export const MyTimeDialog = ({ onClose }) => {
  const refreshTimer = useTimerStore((s) => s.refresh);
  const [data, setData] = useState(null);
  const load = useCallback(() => { timerAPI.myLogs().then(setData).catch((e) => toast.error(e.message)); refreshTimer(); }, [refreshTimer]);
  useEffect(() => { load(); }, [load]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="my-time-dialog">
        <DialogHeader>
          <DialogTitle>Mi tiempo de hoy {data && `· ${fmtMinutes(data.total_minutes)}`}</DialogTitle>
          <DialogDescription>Puedes corregir tus registros de hoy. Para días anteriores, pídeselo a Karmele o Marta.</DialogDescription>
        </DialogHeader>
        {!data ? <Loader2 className="w-5 h-5 animate-spin" /> : (
          <div className="max-h-[45vh] overflow-y-auto">
            {data.logs.length === 0 ? <p className="text-sm text-muted-foreground py-4">Aún no hay tiempo registrado hoy.</p>
              : data.logs.map((l) => <LogRow key={l.id} l={l} onSaved={load} />)}
          </div>
        )}
        <AddManual onSaved={load} />
      </DialogContent>
    </Dialog>
  );
};
